import { AnimationEvent, CSSProperties, MouseEvent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, ButtonBase, Icon, IconButton, Theme, Typography, styled } from "@mui/material";
import { keyframes } from "@mui/system";
import { visuallyHidden } from "@mui/utils";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import useSwipeToDismiss from "./useSwipeToDismiss";
import {
  EASE_ENTRANCE,
  EASE_IN_OUT,
  EASE_SMOOTH_OUT,
  EXIT_OFFSCREEN,
  HOVER_IN_MS,
  HOVER_OUT_MS,
  hoverTint,
  ICON_SWAP_MS,
  ICON_SWAP_SCALE,
  prefersReducedMotion,
  PROGRESS_FILL_DELAY_MS,
  PROGRESS_FILL_MS,
  ROW_ENTER_DISTANCE,
  ROW_ENTER_MS,
  ROW_EXIT_FADE_MS,
  ROW_EXIT_SLIDE_MS,
} from "./motion";
import { DIVIDER, inset, MONO_FONT, NOTIF_PURPLE, ROW_HOVER, TEXT_MUTED, TEXT_TIME } from "./palette";
import { landDetailLink, AppRoutes } from "../../config";
import {
  formatRelative,
  iconForType,
  isUploadProgress,
  isUrgent,
  Notification,
  NotificationEntityType,
  PURPLE_BADGE_TYPES,
} from "./types";

// Row enter: a row that arrives while the panel is already open (SSE push, the
// poller's refetch, Load more, a failed dismiss being restored) drops in from just
// above its slot (--distance-base), on the project entrance curve. Rows present
// when the panel opens don't play it: the popover's own open is the entrance, and
// layering a second one on top is exactly what the old Grow + panelReveal did.
const rowEnter = keyframes`
  0%   { opacity: 0; transform: translate3d(0, -${ROW_ENTER_DISTANCE}px, 0); }
  100% { opacity: 1; transform: translate3d(0, 0, 0); }
`;

// Row exit (× dismiss, Clear all): the same leftward fling as the swipe commit in
// useSwipeToDismiss, so every way of clearing a row feels like one gesture. The
// slide and the fade are separate keyframes because the gesture times them
// separately (220ms on the entrance curve vs a 200ms *linear* fade, which keeps
// the row visible while it travels instead of vanishing in the first few frames).
// -100% is the row's own width, so no measuring is needed.
const rowExitSlide = keyframes`
  0%   { transform: translate3d(0, 0, 0); }
  100% { transform: translate3d(-100%, 0, 0); }
`;
const rowExitFade = keyframes`
  0%   { opacity: 1; }
  100% { opacity: 0; }
`;

// Upload progress fill: grows from the track's leading edge. Only the start pose is
// given; the end is the fill's own transform (scaleX of its share), so one keyframe
// serves every percentage. `backwards` fill, not `both`: once it ends the animation
// lets go, and a live progress update then plays through the fill's transition
// instead of being pinned by a held animation. scaleX only, on a 10px bar.
const progressFill = keyframes`
  from { transform: scaleX(0); }
`;

// Draft upload progress (LAND_UPLOAD_IN_PROGRESS) as a 0–1 share of the wizard's steps.
// Duplicate indexes can't inflate it, and an API without progress reads as no steps.
const progressShare = (notification: Notification): number => {
  const progress = notification.progress;
  if (!progress?.totalSteps) {
    return 0;
  }
  return Math.min(1, new Set(progress.completedSteps).size / progress.totalSteps);
};

// Maps a notification's entity to a real in-app route. Unlike the generic spec
// (which invented /eft/:id, /auction/:id, /trust) we resolve against the project
// route map: EFT/AUCTION tokens open the land detail view, TRUST entities go to
// the trust dashboard. Returns null when there's nothing to link to.
//
// The land view takes a land id. Contract-event notifications carry the NFT id in
// entityId and ECO_TOKEN ones a verification requestId, so the API's resolved landId
// wins whatever the entity type; entityId is only the fallback for an API that
// predates it.
const buildEntityLink = (
  entityType: NotificationEntityType,
  entityId: string | null,
  landId?: number | null
): string | null => {
  if (landId != null) return landDetailLink(landId);
  if (!entityType) return null;
  switch (entityType) {
    case "EFT":
    case "AUCTION": {
      const land = landId ?? entityId;
      return land != null ? landDetailLink(land) : null;
    }
    case "TRUST":
      return AppRoutes.Profile.subRoutes.Dashboard.path;
    default:
      return null;
  }
};

/** "button" rows still need their exit animation; "swipe" rows are already off-screen. */
export type DismissSource = "button" | "swipe";

interface NotificationItemProps {
  notification: Notification;
  onRead: (id: string) => void;
  onDismiss: (id: string, source: DismissSource) => void;
  onNavigate?: () => void;
  /** Mobile only: swap the hover × for a swipe-left-to-clear gesture. */
  swipeToDismiss?: boolean;
  /** Play the enter animation when this row mounts. Read once, at mount. */
  animateIn?: boolean;
  /**
   * Set while the row is leaving (the context has already dropped it): its stagger
   * delay in ms, or EXIT_OFFSCREEN to just hold its slot, hidden, without animating.
   */
  exitDelay?: number;
}

export const NotificationItem = ({
  notification,
  onRead,
  onDismiss,
  onNavigate,
  swipeToDismiss = false,
  animateIn = false,
  exitDelay,
}: NotificationItemProps) => {
  const navigate = useNavigate();
  const urgent = isUrgent(notification.type);
  const purpleBadge = PURPLE_BADGE_TYPES.includes(notification.type);
  const IconComponent = useMemo(() => iconForType(notification.type), [notification.type]);
  const link = buildEntityLink(notification.entityType, notification.entityId, notification.landId);
  // The land's thumbnail stands in for the type icon, which moves to a corner chip.
  // A thumb that fails to load drops back to the plain icon badge.
  const [thumbFailed, setThumbFailed] = useState(false);
  const thumb = notification.contentThumb && !thumbFailed ? notification.contentThumb : null;
  // Draft upload rows aren't a link: they carry their own two actions instead.
  const uploadProgress = isUploadProgress(notification.type);

  // Swipe-left commits a dismiss. Dismissing already drops the row from the feed
  // and decrements the unread badge (the server also excludes dismissed rows from
  // the unread count) — so it is the "mark as read and clear" action. We must not
  // also call onRead here: both read the same stale `items` snapshot and would
  // double-decrement the badge.
  const { ref, backdropRef, handlers } = useSwipeToDismiss({
    enabled: swipeToDismiss,
    onCommit: () => onDismiss(notification.id, "swipe"),
  });

  // Enter is decided once, at mount (a row that mounts later, e.g. prepended by the
  // poller, animates; the rows the panel opens with don't). The class comes off on
  // animationend, so a finished animation never keeps the row on its own layer.
  const [entering, setEntering] = useState(animateIn && !prefersReducedMotion);
  const exitClass = exitDelay === undefined ? "" : exitDelay === EXIT_OFFSCREEN ? "notif-exit-hidden" : "notif-exiting";
  // Applied to the row's outermost element (Row on desktop, SwipeContainer on
  // mobile) so the divider and swipe backdrop travel with it.
  const presence = {
    className: exitClass || (entering ? "notif-entering" : ""),
    style: exitDelay && exitDelay > 0 ? ({ animationDelay: `${exitDelay}ms` } as CSSProperties) : undefined,
    onAnimationEnd: (event: AnimationEvent<HTMLElement>) => {
      // animationend bubbles (ripples, …): only react to the row's own animation.
      if (event.target === event.currentTarget && entering) {
        setEntering(false);
      }
    },
    // FLIP target (NotificationPanel slides siblings into a freed slot) and the
    // hook the Clear all cascade uses to find the first on-screen row.
    "data-flip-id": notification.id,
    "data-notif-row": "",
  };

  const handleClick = () => {
    if (!notification.isRead) onRead(notification.id);
    if (link) {
      onNavigate?.();
      navigate(link);
    }
  };

  const handleDismiss = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onDismiss(notification.id, "button");
  };

  // "Complete draft" resumes the wizard on this land. The type's entityId is the land
  // id, so it stands in when the API's resolved landId is missing.
  const draftLandId = notification.landId ?? (notification.entityId ? Number(notification.entityId) : null);
  const handleCompleteDraft = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!notification.isRead) {
      onRead(notification.id);
    }
    if (draftLandId == null || Number.isNaN(draftLandId)) {
      return;
    }
    onNavigate?.();
    navigate(`${AppRoutes.Upload.path}?tokenId=${draftLandId}`);
  };

  const badge = (
    <>
      {/* Figma: the accent tint is an unread signal. A read row's badge drops to
          neutral grey (left frame of the design), so colour and the dot agree. */}
      <Badge
        className={[purpleBadge && !notification.isRead ? "purple" : "", thumb ? "has-thumb" : ""]
          .filter(Boolean)
          .join(" ")}
        aria-hidden
      >
        {thumb ? (
          <>
            {/* ≤256px static WebP (never the original GIF: those freeze mobile). */}
            <img
              className="notif-thumb"
              src={thumb}
              alt=""
              loading="lazy"
              decoding="async"
              draggable={false}
              onError={() => setThumbFailed(true)}
            />
            <span className="notif-type-chip">
              <Icon component={IconComponent} sx={{ fontSize: 12 }} />
            </span>
          </>
        ) : (
          <Icon component={IconComponent} sx={{ fontSize: 20 }} />
        )}
      </Badge>
    </>
  );

  const share = uploadProgress ? progressShare(notification) : 0;
  const percent = Math.round(share * 100);
  const progressInner = (
    <>
      {badge}
      {/* Figma's progress row: title, subline, bar, "<n>% uploaded…" under the bar's
          right end, then the two text actions. No timestamp, dot or hover ×: Cancel is
          the dismiss. The subline is the server's draft copy, not the design's "Please
          wait while we upload your file": nothing is uploading while a draft waits. */}
      <Content className="progress">
        <Typography className="notif-title">
          {notification.landName ? `Uploading ‘${notification.landName}’` : "Uploading your land"}
        </Typography>
        <Typography className="notif-body">{notification.body}</Typography>
        <ProgressTrack
          role="progressbar"
          aria-label="Draft progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
        >
          <ProgressFill style={{ "--notif-progress": share } as CSSProperties} />
        </ProgressTrack>
        <Typography className="notif-progress-label" aria-hidden>
          {percent}% uploaded…
        </Typography>
        <ProgressActions>
          {/* Dismisses this notification only; the draft itself is untouched. */}
          <TextAction className="cancel" onClick={handleDismiss}>
            Cancel
          </TextAction>
          <TextAction className="primary" onClick={handleCompleteDraft}>
            Complete draft
          </TextAction>
        </ProgressActions>
      </Content>
    </>
  );

  const rowInner = (
    <>
      {badge}

      <Content>
        <Typography className="notif-title">{notification.title}</Typography>
        <Typography className="notif-body">{notification.body}</Typography>
      </Content>

      {/* Right column (Figma): the unread dot sits level with the title, the
          timestamp level with the body. The dot and the dismiss × share one slot
          and icon-swap on hover/focus, so the timestamp never moves or hides. On
          mobile the swipe gesture replaces the ×, and the dot just stays. */}
      <Aside>
        <IconSlot>
          {/* Always rendered. On desktop it's the hover/focus × ; on mobile the
              swipe gesture is the visible affordance, so the button is collapsed
              to a screen-reader-only control (still operable by assistive tech and
              revealed on keyboard focus) — keeping per-item dismiss accessible.
              It comes before the dot so `:focus-visible + .notif-dot` can swap the
              dot out when keyboard focus reveals it on mobile. */}
          <DismissButton
            className="notif-dismiss"
            size="small"
            aria-label="Dismiss notification"
            onClick={handleDismiss}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </DismissButton>
          {/* Kept mounted on read rows (hidden by the `.read` rule) so marking a
              row read plays the swap's out-pose instead of the dot blinking away. */}
          <UnreadDot className="notif-dot" aria-hidden />
        </IconSlot>
        <Typography className="notif-time">{formatRelative(notification.createdAt)}</Typography>
      </Aside>
    </>
  );

  const rowClassName = [
    notification.isRead ? "read" : "unread",
    urgent ? "urgent" : "",
    swipeToDismiss ? "swipeable" : "",
    uploadProgress ? "progress" : "",
  ];

  const row = uploadProgress ? (
    <Row
      {...(swipeToDismiss ? {} : presence)}
      className={[...rowClassName, swipeToDismiss ? "" : presence.className].filter(Boolean).join(" ")}
    >
      {progressInner}
    </Row>
  ) : (
    <Row
      {...(swipeToDismiss ? {} : presence)}
      className={[...rowClassName, swipeToDismiss ? "" : presence.className].filter(Boolean).join(" ")}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      sx={{ cursor: link || !notification.isRead ? "pointer" : "default" }}
    >
      {rowInner}
    </Row>
  );

  if (!swipeToDismiss) return row;

  // Mobile: the foreground (the row) slides over a "clear" backdrop. The backdrop
  // is aria-hidden — the action is conveyed by the row's own button semantics.
  return (
    <SwipeContainer {...presence}>
      <SwipeBackdrop ref={backdropRef} aria-hidden>
        <DeleteOutlineIcon sx={{ fontSize: 22 }} />
        <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>Clear</Typography>
      </SwipeBackdrop>
      <SwipeForeground ref={ref} {...handlers}>
        {row}
      </SwipeForeground>
    </SwipeContainer>
  );
};

// Enter / exit for a row's outermost element (Row on desktop, SwipeContainer on
// mobile). One-shot keyframes with `both` fill: the exit holds its end pose until
// NotificationPanel drops the ghost, and the enter class is removed on animationend.
// `will-change` lives on the classes, so it's one-shot by construction (gone with the
// class) and a staggered row is already promoted while it waits out its delay. The
// stagger delay arrives inline.
const presenceStyles = {
  "&.notif-entering": {
    willChange: "transform, opacity",
    animation: `${rowEnter} ${ROW_ENTER_MS}ms ${EASE_ENTRANCE} both`,
  },
  "&.notif-exiting": {
    // The context has already dropped this row: it's a read-only ghost now.
    pointerEvents: "none",
    willChange: "transform, opacity",
    animation: `${rowExitSlide} ${ROW_EXIT_SLIDE_MS}ms ${EASE_ENTRANCE} both, ${rowExitFade} ${ROW_EXIT_FADE_MS}ms linear both`,
  },
  // Clear all ghost that's scrolled out of view: keeps its slot, skips the animation.
  "&.notif-exit-hidden": {
    pointerEvents: "none",
    visibility: "hidden",
  },
} as const;

// Inset hairline above a row (Figma: 0.5px #dedede, inset to the content edges).
// A pseudo-element rather than a border so it can stop short of the panel edges
// while the hover tint still runs full width. Dropped above the feed's first row:
// in the design the panel's top edge is the only thing above it.
const dividerStyles = (theme: Theme) =>
  ({
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: inset,
      right: inset,
      height: "1px",
      backgroundColor: DIVIDER,
      pointerEvents: "none",
      // Resting rule = hover-out timing (see Row's hover block).
      transition: `opacity ${HOVER_OUT_MS}ms ${EASE_SMOOTH_OUT}`,
      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      ...theme.applyStyles("dark", { backgroundColor: "rgba(255, 255, 255, 0.08)" }),
    },
    "&:first-of-type::after": { display: "none" },
  } as const);

const dividerHidden = (ms: number) => ({
  opacity: 0,
  transition: `opacity ${ms}ms ${EASE_SMOOTH_OUT}`,
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
});

const Row = styled(Box)(({ theme }) => {
  const rowTint = hoverTint(theme, { light: ROW_HOVER, dark: "rgba(255, 255, 255, 0.06)" });
  return {
    position: "relative",
    display: "flex",
    // Badge and text block centre on each other (Figma); the right column
    // stretches to the text block's height (see Aside).
    alignItems: "center",
    gap: "14px",
    padding: `26px ${inset}`,
    outline: "none",

    ...presenceStyles,
    ...dividerStyles(theme),

    // Hover / focus feedback: a ::before tint whose *opacity* fades (hover-in 150ms,
    // softer 250ms hover-out) instead of a background-color transition. The tint is
    // the design's opaque warm grey (#e6e6e2), not an alpha black — the ::before sits
    // under the content (hoverTint isolates the row), so opaque is fine. Keyboard
    // focus shows the same layer and doesn't depend on (hover: hover).
    //
    // The tinted block reads as one surface in the design: the hairlines on both of
    // its edges (its own, and the next row's) fade out with it, on the same clocks.
    ...rowTint,
    // (the "@media (hover: hover)" block is further down, after the progress row's
    // overrides, so they can win over it)
    "&:focus-visible::before": {
      opacity: 1,
    },
    "&:focus-visible::after, &:focus-visible + &::after": dividerHidden(HOVER_IN_MS),

    // Urgent treatment: warning accent bar + tinted background.
    "&.urgent": {
      backgroundColor: "rgba(211, 125, 74, 0.08)",
      boxShadow: "inset 3px 0 0 0 #d37d4a",
    },

    // The Figma row has no resting dismiss button: the close affordance only
    // appears on hover/focus, in the unread dot's slot (the timestamp stays put, as
    // in the design's hover frame). Touch devices keep it visible.
    //
    // Dot ↔ × is the icon-swap recipe: the outgoing icon shrinks to --scale 0.25 and
    // fades while the incoming one grows from it, 250ms ease-in-out *both ways* (a
    // swap is one reversible motion, so the polish rules keep it symmetric rather
    // than splitting hover-in/out). The recipe's blur and its permanent will-change
    // are dropped: opacity + transform only, on two small elements. A read row has
    // no dot, so there the × simply grows in alone. Marking a row read sends the
    // dot out through the same pose.
    "& .notif-dismiss, & .notif-dot": {
      transition: `opacity ${ICON_SWAP_MS}ms ${EASE_IN_OUT}, transform ${ICON_SWAP_MS}ms ${EASE_IN_OUT}`,
    },
    "& .notif-dismiss": {
      ...iconOut,
      pointerEvents: "none",
    },
    "&.read .notif-dot": iconOut,
    "&:hover .notif-dismiss, &:focus-within .notif-dismiss": {
      ...iconIn,
      pointerEvents: "auto",
    },
    "&:hover .notif-dot, &:focus-within .notif-dot": iconOut,
    "@media (hover: none)": {
      "& .notif-dismiss": { ...iconIn, pointerEvents: "auto" },
      "& .notif-dot": iconOut,
    },
    "@media (prefers-reduced-motion: reduce)": {
      "&.notif-entering, &.notif-exiting": { animation: "none" },
      "& .notif-dismiss, & .notif-dot": { transition: "none" },
    },

    // In the swipeable (mobile) layout the SwipeContainer owns the divider so the
    // line doesn't slide away with the row during a swipe, the timestamp stays
    // visible, and the × collapses to a screen-reader-only control (swipe-left is
    // the visible affordance). It re-appears for keyboard focus so the action is
    // still reachable without a pointer.
    "&.swipeable": {
      "&::after": { display: "none" },
      // The dot stays: a tap focuses the row (focus-within) without revealing the
      // visually-hidden ×, so the slot would otherwise go empty. Only keyboard
      // focus on the × itself swaps the dot out (rule below).
      "&:focus-within .notif-dot, & .notif-dot": iconIn,
      "&.read .notif-dot": iconOut,
      "& .notif-dismiss:focus-visible + .notif-dot": iconOut,
      "& .notif-dismiss": {
        ...visuallyHidden,
        // Keep it activatable by assistive tech while visually clipped.
        pointerEvents: "auto",
      },
      "& .notif-dismiss:focus-visible": {
        position: "absolute",
        top: "-8px",
        right: "-8px",
        width: "28px",
        height: "28px",
        margin: 0,
        clip: "auto",
        overflow: "visible",
        whiteSpace: "normal",
        opacity: 1,
        transform: "none",
      },
    },

    // Draft upload row (Figma: 21px above the title, 18px below the actions; the badge
    // sits level with the title rather than centred on the whole block). Not a link,
    // so it gets no hover/focus tint, and its hairlines stay put.
    "&.progress": {
      alignItems: "flex-start",
      padding: `21px ${inset} 18px`,
      cursor: "default",
      "&::before": { display: "none" },
    },
    "@media (hover: hover)": {
      ...(rowTint["@media (hover: hover)"] as object),
      "&:hover::after, &:hover + &::after": dividerHidden(HOVER_IN_MS),
      "&.progress:hover::after, &.progress:hover + &::after": { opacity: 1 },
    },

    // No dark `&.swipeable` (or `&::after`) override here: without CSS vars,
    // applyStyles' object is spread shallowly, so one would replace the whole block
    // above (visually-hidden ×, pinned timestamp, hidden divider). The divider's dark
    // colour lives inside dividerStyles' own `&::after` instead.
    ...theme.applyStyles("dark", {
      "&.urgent": {
        backgroundColor: "rgba(211, 125, 74, 0.16)",
        boxShadow: "inset 3px 0 0 0 #d37d4a",
      },
    }),
  };
});

// ── Swipe-to-clear scaffolding (mobile only) ──────────────────────────────────
// SwipeContainer clips the row as it flies off the leading edge and carries the
// row divider. SwipeBackdrop is the "clear" surface revealed beneath. The row
// lives inside SwipeForeground, which is the only layer that translates.
const SwipeContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  // Opaque base so nothing shows through before the backdrop fades in.
  backgroundColor: theme.palette.back.infoCard,
  ...presenceStyles,
  ...dividerStyles(theme),
  // The divider paints over the foreground (z 1), which is opaque.
  "&::after": { ...dividerStyles(theme)["&::after"], zIndex: 2 },
  "@media (prefers-reduced-motion: reduce)": {
    "&.notif-entering, &.notif-exiting": { animation: "none" },
  },
  ...theme.applyStyles("dark", {
    backgroundColor: "#1a1a1a",
  }),
}));

// The icon + "Clear" sit in the middle of the row and stay there: the row slides
// off them, so they're fully uncovered once the swipe passes halfway (the commit
// fling always gets there). Static, so the gesture never has to move them.
const SwipeBackdrop = styled(Box)(({ theme }) => ({
  position: "absolute",
  inset: 0,
  zIndex: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  color: "#fff",
  backgroundColor: "#d9483f",
  opacity: 0,
  // No persistent will-change: the gesture promotes/clears it per swipe (per
  // project motion conventions — promotion is one-shot, not left on at rest).
  ...theme.applyStyles("dark", {
    backgroundColor: "#b23a33",
  }),
}));

const SwipeForeground = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  // Vertical scrolling still pans the feed; horizontal drags are handed to JS
  // (no preventDefault needed), so the row swipe never fights the scroll.
  touchAction: "pan-y",
  // Opaque so the backdrop only shows where the row has slid away. No resting
  // transform/will-change here — the gesture sets and clears them one-shot.
  backgroundColor: theme.palette.back.infoCard,
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none !important",
  },
  ...theme.applyStyles("dark", {
    backgroundColor: "#1a1a1a",
  }),
}));

// Right-hand column, stretched to the text block's height: the dot / × slot pins
// to the top (level with the title), the timestamp to the bottom (level with the
// body). The × overlays the dot rather than taking space, so the row width is stable.
const Aside = styled(Box)({
  position: "relative",
  alignSelf: "stretch",
  flexShrink: 0,
  minWidth: "56px",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",

  "& .notif-time": {
    marginTop: "auto",
    fontFamily: MONO_FONT,
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: "17px",
    textTransform: "uppercase",
    color: TEXT_TIME,
    whiteSpace: "nowrap",
    textAlign: "right",
  },
});

// The icon-swap's two poses (opacity + transform only; see Row).
const iconIn = { opacity: 1, transform: "scale(1)" } as const;
const iconOut = { opacity: 0, transform: `scale(${ICON_SWAP_SCALE})` } as const;

// The dot's 12px box, 3px below the title's top (Figma "Frame 1041"). The 28px ×
// is centred on it, overhanging 8px on each side, so both icons share one centre.
const IconSlot = styled("span")({
  position: "relative",
  width: "12px",
  height: "12px",
  marginTop: "3px",
  flexShrink: 0,
});

const UnreadDot = styled("span")({
  position: "absolute",
  inset: 0,
  borderRadius: "50%",
  backgroundColor: NOTIF_PURPLE,
});

// 36px circular badge with a 20px black glyph. Neutral grey (#ccc, the theme's
// border.default) by default and for every read row; purple while a Guardian
// status row is unread.
//
// With a land thumbnail (EFT notifications) the circle shows the land instead,
// cropped to fill, over the grey so a slow image never leaves a hole. The type icon
// moves to a 16px chip on the lower-right edge, ringed in the panel colour so it
// reads as cut out of the photo; the chip carries the purple/grey state.
const Badge = styled(Box)(({ theme }) => ({
  position: "relative",
  flexShrink: 0,
  width: "36px",
  height: "36px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.border.default,
  color: "#000",

  "&.purple": {
    backgroundColor: NOTIF_PURPLE,
    color: "#000",
  },
  // Progress row: 3px below the title's top (Figma 24 vs 21).
  ".progress > &": {
    marginTop: "3px",
  },

  "& .notif-thumb": {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    objectFit: "cover",
    display: "block",
  },
  "& .notif-type-chip": {
    position: "absolute",
    right: "-4px",
    bottom: "-4px",
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.border.default,
    color: "#000",
    boxShadow: `0 0 0 2px ${theme.palette.back.infoCard}`,
  },
  "&.has-thumb": {
    backgroundColor: theme.palette.border.default,
  },
  "&.has-thumb.purple .notif-type-chip": {
    backgroundColor: NOTIF_PURPLE,
  },

  ...theme.applyStyles("dark", {
    backgroundColor: "rgba(255, 255, 255, 0.12)",
    color: "rgba(255, 255, 255, 0.85)",
    "& .notif-type-chip": {
      position: "absolute",
      right: "-4px",
      bottom: "-4px",
      width: "16px",
      height: "16px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      // Opaque (the grey badge tint is translucent) so the photo doesn't show through.
      backgroundColor: "#3a3a3a",
      color: "rgba(255, 255, 255, 0.85)",
      boxShadow: "0 0 0 2px #1a1a1a",
    },
    "&.has-thumb": { backgroundColor: "rgba(255, 255, 255, 0.12)" },
  }),
}));

// Title: Inter Medium 14/21, primary text. Body: Inter Regular 14, muted, 2px below.
// The progress row's subline sits flush under the title on a 22px line (Figma).
const Content = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  "&.progress .notif-body": {
    marginTop: 0,
    lineHeight: "22px",
  },
  // Inter SemiBold 12/22 #999, right-aligned under the bar's end.
  "& .notif-progress-label": {
    marginTop: "8px",
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: "22px",
    textAlign: "right",
    color: TEXT_TIME,
  },
  "& .notif-title": {
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "21px",
    color: theme.palette.text.primary,
  },
  "& .notif-body": {
    marginTop: "2px",
    fontSize: "14px",
    lineHeight: "17px",
    color: TEXT_MUTED,
    ...theme.applyStyles("dark", { color: "rgba(255, 255, 255, 0.6)" }),
  },
}));

// Figma: a 10px pill, white track, purple fill, 9px under the subline. The track clips
// the fill so its leading end keeps the pill's round cap at any scale.
const ProgressTrack = styled("div")(({ theme }) => ({
  position: "relative",
  marginTop: "9px",
  height: "10px",
  borderRadius: "10px",
  overflow: "hidden",
  backgroundColor: "#fff",
  ...theme.applyStyles("dark", { backgroundColor: "rgba(255, 255, 255, 0.12)" }),
}));

// The fill is the full track, scaled down to the draft's share (--notif-progress, 0–1)
// from its leading edge: transform only, so neither the first grow nor a live update
// (SSE rewrite of the row) re-lays out the row.
const ProgressFill = styled("span")({
  position: "absolute",
  inset: 0,
  borderRadius: "inherit",
  backgroundColor: NOTIF_PURPLE,
  transformOrigin: "0 50%",
  transform: "scaleX(var(--notif-progress))",
  animation: `${progressFill} ${PROGRESS_FILL_MS}ms ${EASE_ENTRANCE} ${PROGRESS_FILL_DELAY_MS}ms backwards`,
  transition: `transform ${PROGRESS_FILL_MS}ms ${EASE_ENTRANCE}`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    transition: "none",
  },
});

// Figma: Cancel (Inter Regular 14, #333) and the primary action (Inter Bold 14, black),
// 16px apart, on a 22px line whose box overlaps the percentage's by 8px (the two
// sit at opposite ends). Padded out to a 30px hit area without moving the layout.
const ProgressActions = styled(Box)({
  display: "flex",
  gap: "16px",
  marginTop: "-8px",
});

const TextAction = styled(ButtonBase)(({ theme }) => ({
  padding: "4px 0",
  margin: "-4px 0",
  fontFamily: "inherit",
  fontSize: "14px",
  lineHeight: "22px",
  borderRadius: "4px",
  "&.cancel": { fontWeight: 400, color: "#333" },
  "&.primary": { fontWeight: 700, color: "#000" },
  "@media (hover: hover)": {
    "&:hover": { textDecoration: "underline" },
  },
  "&:focus-visible": {
    outline: `2px solid ${NOTIF_PURPLE}`,
    outlineOffset: "2px",
  },
  ...theme.applyStyles("dark", {
    "&.cancel": { fontWeight: 400, color: "rgba(255, 255, 255, 0.7)" },
    "&.primary": { fontWeight: 700, color: "#fff" },
  }),
}));

// Centred on the timestamp it replaces (bottom of the right column).
const DismissButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "-8px",
  right: "-8px",
  width: "28px",
  height: "28px",
  padding: 0,
  color: TEXT_MUTED,
  // Circle tint fades on opacity (::before) rather than MUI's background-color
  // transition; the icon's colour change stays instant.
  ...hoverTint(theme, { light: "rgba(0, 0, 0, 0.06)", dark: "rgba(255, 255, 255, 0.1)" }),
  "&:hover": {
    backgroundColor: "transparent",
    color: theme.palette.text.primary,
  },
}));

export default NotificationItem;
